import { Updater } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import { KnowledgeListFilters } from "@intelligems/ig-types";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";

interface KnowledgeFiltersProps {
  filters: KnowledgeListFilters;
  categories: string[];
  symptoms: string[];
  rootCauses: string[];
  setFilters: Updater<KnowledgeListFilters>;
}

export const KnowledgeFilters = ({
  filters,
  categories,
  symptoms,
  rootCauses,
  setFilters,
}: KnowledgeFiltersProps) => {
  const handleSourceChange = (source: string) => {
    setFilters((draft) => {
      draft.source = source;
      draft.categories = [];
      draft.symptoms = [];
      draft.rootCauses = [];
      draft.page = 1;
    });
  };

  const handleStatusChange = (status: KnowledgeListFilters["status"]) => {
    setFilters((draft) => {
      draft.status = status;
      draft.page = 1;
    });
  };

  const handleCategoryToggle = (category: string) => {
    setFilters((draft) => {
      const isSelected = draft.categories.includes(category);
      if (isSelected) {
        draft.categories = draft.categories.filter(
          (value) => value !== category
        );
      } else {
        draft.categories.push(category);
      }
      draft.page = 1;
    });
  };

  const handleSymptomToggle = (symptom: string) => {
    setFilters((draft) => {
      const isSelected = draft.symptoms.includes(symptom);
      if (isSelected) {
        draft.symptoms = draft.symptoms.filter((value) => value !== symptom);
      } else {
        draft.symptoms.push(symptom);
      }
      draft.page = 1;
    });
  };

  const handleRootCauseToggle = (rootCause: string) => {
    setFilters((draft) => {
      const isSelected = draft.rootCauses.includes(rootCause);
      if (isSelected) {
        draft.rootCauses = draft.rootCauses.filter(
          (value) => value !== rootCause
        );
      } else {
        draft.rootCauses.push(rootCause);
      }
      draft.page = 1;
    });
  };

  return (
    <Stack spacing={2} sx={{ p: 2, ml: 2 }}>
      <FormControl component="fieldset">
        <IgTypography variant="subtitle1" sx={{ mb: 1 }}>
          Source
        </IgTypography>
        <RadioGroup
          value={filters.source}
          onChange={(_event, value) => handleSourceChange(value)}
        >
          <FormControlLabel value="all" control={<Radio />} label="All" />
          <FormControlLabel
            value="pylon-solutions"
            control={<Radio />}
            label="Pylon Solutions"
          />
          <FormControlLabel value="notion" control={<Radio />} label="Notion" />
          <FormControlLabel
            value="docs-repo"
            control={<Radio />}
            label="Docs"
          />
          <FormControlLabel
            value="headless-docs"
            control={<Radio />}
            label="Headless Docs"
          />
          <FormControlLabel
            value="internal-docs"
            control={<Radio />}
            label="Internal Docs"
          />
        </RadioGroup>
      </FormControl>

      <Divider />

      <FormControl component="fieldset">
        <IgTypography variant="subtitle1" sx={{ mb: 1 }}>
          Status
        </IgTypography>
        <RadioGroup
          value={filters.status}
          onChange={(_event, value) =>
            handleStatusChange(value as KnowledgeListFilters["status"])
          }
        >
          <FormControlLabel value="all" control={<Radio />} label="All" />
          <FormControlLabel
            value="pending"
            control={<Radio />}
            label="Pending"
          />
          <FormControlLabel
            value="verified"
            control={<Radio />}
            label="Verified"
          />
        </RadioGroup>
      </FormControl>

      <Divider />

      <FormControl component="fieldset">
        <IgTypography variant="subtitle1" sx={{ mb: 1 }}>
          Category
        </IgTypography>
        <FormGroup>
          {categories.map((category) => (
            <FormControlLabel
              sx={{ pl: 1 }}
              key={category}
              control={
                <Checkbox
                  checked={filters.categories.includes(category)}
                  onChange={() => handleCategoryToggle(category)}
                />
              }
              label={category}
            />
          ))}
        </FormGroup>
      </FormControl>

      {filters.source === "pylon-solutions" && symptoms.length > 0 && (
        <>
          <Divider />
          <FormControl component="fieldset">
            <IgTypography variant="subtitle2" sx={{ mb: 1 }}>
              Symptom
            </IgTypography>
            <FormGroup>
              {symptoms.map((symptom) => (
                <FormControlLabel
                  sx={{ pl: 1 }}
                  key={symptom}
                  control={
                    <Checkbox
                      checked={filters.symptoms.includes(symptom)}
                      onChange={() => handleSymptomToggle(symptom)}
                    />
                  }
                  label={symptom}
                />
              ))}
            </FormGroup>
          </FormControl>
        </>
      )}

      {filters.source === "pylon-solutions" && rootCauses.length > 0 && (
        <>
          <Divider />
          <FormControl component="fieldset">
            <IgTypography variant="subtitle2" sx={{ mb: 1 }}>
              Root Cause
            </IgTypography>
            <FormGroup>
              {rootCauses.map((rootCause) => (
                <FormControlLabel
                  sx={{ pl: 1 }}
                  key={rootCause}
                  control={
                    <Checkbox
                      checked={filters.rootCauses.includes(rootCause)}
                      onChange={() => handleRootCauseToggle(rootCause)}
                    />
                  }
                  label={rootCause}
                />
              ))}
            </FormGroup>
          </FormControl>
        </>
      )}
    </Stack>
  );
};
